import React, { useCallback, useEffect, useState } from "react";
import {
  LayoutGrid, Newspaper, Clapperboard, MapPin, Trophy, Cloud, Car, Mic, CalendarClock, BarChart3, Radio,
} from "lucide-react";
import { api } from "@/lib/api";
import { NewsroomTab } from "@/components/newsroom/NewsroomTab";
import { SectionTab } from "@/components/newsroom/SectionTab";
import { WeatherTab } from "@/components/newsroom/WeatherTab";
import { TrafficTab } from "@/components/newsroom/TrafficTab";
import { AnchorStudioTab } from "@/components/newsroom/AnchorStudioTab";
import { ScheduleTab } from "@/components/newsroom/ScheduleTab";
import { NewsroomAnalyticsTab } from "@/components/newsroom/NewsroomAnalyticsTab";

const TABS = [
  { id: "newsroom", label: "Newsroom", icon: LayoutGrid },
  { id: "music", label: "Music News", icon: Newspaper },
  { id: "entertainment", label: "Entertainment", icon: Clapperboard },
  { id: "local", label: "Local News", icon: MapPin },
  { id: "sports", label: "Sports", icon: Trophy },
  { id: "weather", label: "Weather", icon: Cloud },
  { id: "traffic", label: "Traffic", icon: Car },
  { id: "anchor", label: "AI Anchor", icon: Mic },
  { id: "schedule", label: "Schedule", icon: CalendarClock },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
];

const MUSIC_GENRES = ["R&B", "Reggae", "Pop", "Hip-Hop", "Neo Soul", "Gospel", "Disco", "Independent Artist", "Dance", "Country", "Latin", "Caribbean"]
  .map((g) => ({ id: g, label: g }));
const ENT_CHIPS = [["celebrity", "Celebrity"], ["television", "Television"], ["movies", "Movies"], ["awards", "Awards Shows"], ["festivals", "Festivals"], ["streaming", "Streaming"]]
  .map(([id, label]) => ({ id, label }));
const LOCAL_CHIPS = [["headlines", "Headlines"], ["community", "Community Events"], ["government", "Government"], ["emergency", "Emergency"], ["public_notices", "Public Notices"]]
  .map(([id, label]) => ({ id, label }));
const SPORT_CHIPS = [["nfl", "NFL"], ["nba", "NBA"], ["mlb", "MLB"], ["soccer", "Soccer"], ["cricket", "Cricket"], ["olympics", "Olympics"], ["tennis", "Tennis"], ["boxing", "Boxing"]]
  .map(([id, label]) => ({ id, label }));

export default function NewsCenterHub() {
  const [tab, setTab] = useState("newsroom");
  const [stations, setStations] = useState([]);
  const [stationId, setStationId] = useState("");
  const [summary, setSummary] = useState(null);
  const [stories, setStories] = useState([]);
  const [anchorPrefill, setAnchorPrefill] = useState("");

  const loadSummary = useCallback(() => api.get("/newsroom/summary").then((r) => setSummary(r.data)).catch(() => {}), []);
  const loadStories = useCallback(() => api.get("/news/stories", { params: { limit: 120 } }).then((r) => setStories(r.data || [])).catch(() => {}), []);
  const reload = useCallback(async () => { await Promise.all([loadSummary(), loadStories()]); }, [loadSummary, loadStories]);

  useEffect(() => {
    api.get("/stations").then((r) => {
      const list = r.data || [];
      setStations(list);
      setStationId(list[0]?.station_id || "");
    }).catch(() => {});
    reload();
  }, [reload]);

  const openAnchorWithScript = (script) => { setAnchorPrefill(script); setTab("anchor"); };
  const sectionProps = { stories, reload };

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="news-center-hub-page">
      <div className="flex flex-wrap items-center gap-4 mb-5">
        <div className="rounded-xl bg-orange-500/15 p-2.5"><Radio className="h-6 w-6 text-orange-400" /></div>
        <div className="flex-1 min-w-[220px]">
          <h1 className="font-display text-2xl font-bold text-ink-primary">AI News &amp; Content Center</h1>
          <p className="text-xs text-ink-muted">Virtual Newsroom · 24/7 AI-Powered · Real Sources Only</p>
        </div>
        {stations.length > 1 && (
          <select value={stationId} onChange={(e) => setStationId(e.target.value)} data-testid="newsroom-station-select"
            className="rounded-lg border border-line bg-bg-surface px-3 py-2 text-sm text-ink-primary focus:outline-none focus:border-brand-amber/50">
            {stations.map((s) => <option key={s.station_id} value={s.station_id}>{s.name}</option>)}
          </select>
        )}
        <span className="inline-flex items-center gap-1.5 text-xs font-bold text-rose-400" data-testid="newsroom-live-dot">
          <span className="h-2 w-2 rounded-full bg-rose-500 animate-pulse" /> LIVE
        </span>
      </div>

      <div className="flex gap-1 overflow-x-auto border-b border-line mb-8" data-testid="newsroom-tabs">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`newsroom-tab-${t.id}`}
            className={`inline-flex items-center gap-1.5 whitespace-nowrap px-3.5 py-3 text-sm font-medium border-b-2 -mb-px transition ${
              tab === t.id ? "border-brand-amber text-ink-primary" : "border-transparent text-ink-muted hover:text-ink-secondary"}`}>
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "newsroom" && <NewsroomTab summary={summary} refresh={reload} setTab={setTab} />}
      {tab === "music" && (
        <SectionTab section="music" title="Music News Center" subtitle="Billboard · Rolling Stone · Pitchfork · Complex · Grammy — real feeds"
          chips={MUSIC_GENRES} chipMode="filter" categories={["music", "industry"]} fetchLabel="Fetch Music News"
          accent="from-pink-500 to-rose-500" {...sectionProps} />
      )}
      {tab === "entertainment" && (
        <SectionTab section="entertainment" title="Entertainment News" subtitle="Celebrity · TV · Movies · Awards · Festivals · Streaming — via Google News"
          chips={ENT_CHIPS} chipMode="topic" categories={["entertainment"]} fetchLabel="Fetch Entertainment News"
          accent="from-violet-500 to-fuchsia-500" {...sectionProps} />
      )}
      {tab === "local" && (
        <SectionTab section="local" title="Local News Center" subtitle="Hyperlocal news tailored to your station's market — via Google News"
          chips={LOCAL_CHIPS} chipMode="topic" categories={["local"]} fetchLabel="Fetch Local News" hasLocation
          accent="from-sky-500 to-cyan-500" {...sectionProps} />
      )}
      {tab === "sports" && (
        <SectionTab section="sports" title="Sports News Center" subtitle="NFL · NBA · MLB · Soccer · Cricket · Olympics — via Google News"
          chips={SPORT_CHIPS} chipMode="topic" categories={["sports"]} fetchLabel="Fetch Sports News"
          accent="from-amber-500 to-orange-500" {...sectionProps} />
      )}
      {tab === "weather" && <WeatherTab stationId={stationId} reload={reload} />}
      {tab === "traffic" && <TrafficTab stationId={stationId} reload={reload} />}
      {tab === "anchor" && <AnchorStudioTab stationId={stationId} prefill={anchorPrefill} />}
      {tab === "schedule" && <ScheduleTab stationId={stationId} onScript={openAnchorWithScript} />}
      {tab === "analytics" && <NewsroomAnalyticsTab />}
    </div>
  );
}
